import { useEffect, useRef } from 'react';

import { useVirtualizer } from '@tanstack/react-virtual';

interface UseVirtualizedListProps<T> {
  items: T[];
  hasMore: boolean;
  loading?: boolean;
  estimateSize?: number;
  next: () => void;
}

export const useVirtualizedList = <T>({
  items,
  hasMore,
  loading,
  estimateSize = 80,
  next,
}: UseVirtualizedListProps<T>) => {
  const parentRef = useRef<HTMLUListElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: hasMore ? items.length + 1 : items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateSize,
    measureElement: el => el?.getBoundingClientRect().height,
    overscan: 5,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  useEffect(() => {
    const [lastItem] = [...virtualItems].reverse();

    if (!lastItem) {
      return;
    }

    if (lastItem.index >= items.length - 1 && hasMore && !loading) {
      next();
    }
  }, [hasMore, next, items.length, loading, virtualItems]);

  return {
    parentRef,
    rowVirtualizer,
    virtualItems,
    loading,
  };
};
